import type { PracticeReviewInput } from "@wmf/shared/practiceReview";
import { TUTOR_RECOGNITIONS } from "@wmf/shared/studentAwards";
import type { Attendance } from "./api.js";

/**
 * Private note recovery is tab-scoped, expires after 24 hours, and is cleared
 * when this tab signs out or changes account. A record contains only the
 * write-up, note attendance and validated review inputs; File objects and
 * attachment/task-tool writes are outside this editor's recovery contract.
 *
 * sessionStorage keeps ordinary tabs independent. A duplicated tab may inherit
 * its opener's record, so both can restore the same base revision; the server's
 * note version and each review version decide which save is accepted, and a
 * later writer receives the existing stale conflict instead of overwriting.
 * Malformed and expired values are removed and never applied.
 */
const STORAGE_PREFIX = "wmf:private-lesson-draft:v1:";
const SCHOOL_STORAGE_PREFIX = "wmf:school-lesson-draft:v1:";
const RETENTION_MS = 24 * 60 * 60 * 1000;
const PRACTICE_REVIEW_KEYS = new Set([
  "todoId",
  "effortRating",
  "progressRating",
  "comment",
  "complete",
  "recognitions",
  "readyForReviewAt",
  "version",
]);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const OFFSET_DATE_TIME_PATTERN = /^\d{4}-\d\d-\d\dT\d\d:\d\d(?::\d\d(?:\.\d+)?)?(?:Z|[+-]\d\d:\d\d)$/;

export interface PrivateLessonDraft {
  version: 1;
  accountId: string;
  domain: "private";
  bookingId: string;
  lessonId: string | null;
  baseRevision: string | null;
  savedAt: string;
  title: string;
  content: string;
  attendance: Attendance | "";
  practiceReviews: PracticeReviewInput[];
}

export interface SchoolLessonDraft {
  version: 1;
  accountId: string;
  domain: "school";
  sessionId: string;
  pupilId: string;
  noteId: string | null;
  baseRevision: string | null;
  baseVersion: number | null;
  savedAt: string;
  title: string;
  content: string;
  attendance: "" | "present" | "late" | "absent" | "cancelled";
  practiceReviews: PracticeReviewInput[];
}

export type LessonDraftRead<T> =
  | { state: "empty" }
  | { state: "available"; draft: T }
  | { state: "expired" | "invalid" | "unavailable" };

export type PrivateLessonDraftRead = LessonDraftRead<PrivateLessonDraft>;
export type SchoolLessonDraftRead = LessonDraftRead<SchoolLessonDraft>;

export interface PrivateLessonRecoveryStatus {
  dirty: boolean;
  persisted: boolean;
}

export const PRIVATE_LESSON_RECOVERY_EVENT = "wmf:private-lesson-recovery";

export function privateLessonDraftKey(accountId: string, bookingId: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(accountId)}:${encodeURIComponent(bookingId)}`;
}

export function schoolLessonDraftKey(accountId: string, sessionId: string, pupilId: string): string {
  return `${SCHOOL_STORAGE_PREFIX}${encodeURIComponent(accountId)}:${encodeURIComponent(sessionId)}:${encodeURIComponent(pupilId)}`;
}

export function readPrivateLessonDraft(accountId: string, bookingId: string): PrivateLessonDraftRead {
  const key = privateLessonDraftKey(accountId, bookingId);
  return readStoredDraft(key, (value) => parsePrivateDraft(value, accountId, bookingId));
}

export function readSchoolLessonDraft(accountId: string, sessionId: string, pupilId: string): SchoolLessonDraftRead {
  const key = schoolLessonDraftKey(accountId, sessionId, pupilId);
  return readStoredDraft(key, (value) => parseSchoolDraft(value, accountId, sessionId, pupilId));
}

function readStoredDraft<T extends { savedAt: string }>(key: string, parse: (value: unknown) => T | null): LessonDraftRead<T> {
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    return { state: "unavailable" };
  }

  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return { state: "unavailable" };
  }
  if (raw === null) return { state: "empty" };

  let draft: T | null = null;
  try {
    draft = parse(JSON.parse(raw) as unknown);
  } catch {
    draft = null;
  }
  if (!draft) {
    try { storage.removeItem(key); } catch { /* The record is ignored even if cleanup is denied. */ }
    return { state: "invalid" };
  }

  if (Date.now() - Date.parse(draft.savedAt) > RETENTION_MS) {
    try { storage.removeItem(key); } catch { /* The expired record is ignored even if cleanup is denied. */ }
    return { state: "expired" };
  }
  return { state: "available", draft };
}

export function writePrivateLessonDraft(draft: PrivateLessonDraft): boolean {
  return writeStoredDraft(privateLessonDraftKey(draft.accountId, draft.bookingId), draft);
}

export function writeSchoolLessonDraft(draft: SchoolLessonDraft): boolean {
  return writeStoredDraft(schoolLessonDraftKey(draft.accountId, draft.sessionId, draft.pupilId), draft);
}

function writeStoredDraft(key: string, draft: PrivateLessonDraft | SchoolLessonDraft): boolean {
  try {
    const storage = window.sessionStorage;
    storage.setItem(key, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function removePrivateLessonDraft(accountId: string, bookingId: string): boolean {
  return removeStoredDraft(privateLessonDraftKey(accountId, bookingId));
}

export function removeSchoolLessonDraft(accountId: string, sessionId: string, pupilId: string): boolean {
  return removeStoredDraft(schoolLessonDraftKey(accountId, sessionId, pupilId));
}

function removeStoredDraft(key: string): boolean {
  try {
    window.sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function clearLessonDraftsForAccount(accountId: string): boolean {
  try {
    const storage = window.sessionStorage;
    const prefixes = [STORAGE_PREFIX, SCHOOL_STORAGE_PREFIX].map((prefix) => `${prefix}${encodeURIComponent(accountId)}:`);
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** Kept as an alias for existing private-editor callers while account cleanup covers both domains. */
export const clearPrivateLessonDraftsForAccount = clearLessonDraftsForAccount;

export function publishPrivateLessonRecoveryStatus(status: PrivateLessonRecoveryStatus): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<PrivateLessonRecoveryStatus>(PRIVATE_LESSON_RECOVERY_EVENT, { detail: status }));
}

function parsePrivateDraft(value: unknown, accountId: string, bookingId: string): PrivateLessonDraft | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const allowedAttendance: Array<Attendance | ""> = ["", "present", "absent", "late", "cancelled"];
  if (
    record["version"] !== 1 ||
    record["accountId"] !== accountId ||
    record["domain"] !== "private" ||
    record["bookingId"] !== bookingId ||
    (record["lessonId"] !== null && typeof record["lessonId"] !== "string") ||
    (record["baseRevision"] !== null && typeof record["baseRevision"] !== "string") ||
    typeof record["savedAt"] !== "string" ||
    !Number.isFinite(Date.parse(record["savedAt"])) ||
    typeof record["title"] !== "string" ||
    typeof record["content"] !== "string" ||
    !allowedAttendance.includes(record["attendance"] as Attendance | "")
  ) return null;

  const practiceReviews = parsePracticeReviews(record["practiceReviews"]);
  if (!practiceReviews) return null;

  return {
    version: 1,
    accountId,
    domain: "private",
    bookingId,
    lessonId: record["lessonId"] as string | null,
    baseRevision: record["baseRevision"] as string | null,
    savedAt: record["savedAt"],
    title: record["title"],
    content: record["content"],
    attendance: record["attendance"] as Attendance | "",
    practiceReviews,
  };
}

function parseSchoolDraft(value: unknown, accountId: string, sessionId: string, pupilId: string): SchoolLessonDraft | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const allowedAttendance: SchoolLessonDraft["attendance"][] = ["", "present", "late", "absent", "cancelled"];
  if (
    record["version"] !== 1 ||
    record["accountId"] !== accountId ||
    record["domain"] !== "school" ||
    record["sessionId"] !== sessionId ||
    record["pupilId"] !== pupilId ||
    (record["noteId"] !== null && typeof record["noteId"] !== "string") ||
    (record["baseRevision"] !== null && typeof record["baseRevision"] !== "string") ||
    (record["baseVersion"] !== undefined && record["baseVersion"] !== null && (typeof record["baseVersion"] !== "number" || !Number.isInteger(record["baseVersion"]) || record["baseVersion"] < 1)) ||
    typeof record["savedAt"] !== "string" ||
    !Number.isFinite(Date.parse(record["savedAt"])) ||
    typeof record["title"] !== "string" ||
    typeof record["content"] !== "string" ||
    !allowedAttendance.includes(record["attendance"] as SchoolLessonDraft["attendance"])
  ) return null;

  const practiceReviews = parsePracticeReviews(record["practiceReviews"]);
  if (!practiceReviews) return null;
  return {
    version: 1,
    accountId,
    domain: "school",
    sessionId,
    pupilId,
    noteId: record["noteId"] as string | null,
    baseRevision: record["baseRevision"] as string | null,
    baseVersion: (record["baseVersion"] as number | null | undefined) ?? null,
    savedAt: record["savedAt"],
    title: record["title"],
    content: record["content"],
    attendance: record["attendance"] as SchoolLessonDraft["attendance"],
    practiceReviews,
  };
}

function parsePracticeReviews(value: unknown): PracticeReviewInput[] | null {
  if (!Array.isArray(value)) return null;
  const practiceReviews: PracticeReviewInput[] = [];
  for (const item of value) {
    const parsed = parsePracticeReview(item);
    if (!parsed) return null;
    practiceReviews.push(parsed);
  }
  return new Set(practiceReviews.map((item) => item.todoId)).size === practiceReviews.length
    ? practiceReviews
    : null;
}

/**
 * Validate the persisted review shape without importing the shared Zod schema
 * into the eager lesson-notes route. The server still performs authoritative
 * validation when these values are saved or published.
 */
function parsePracticeReview(value: unknown): PracticeReviewInput | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (Object.keys(record).some((key) => !PRACTICE_REVIEW_KEYS.has(key))) return null;

  const effortRating = record["effortRating"];
  const progressRating = record["progressRating"];
  const comment = record["comment"];
  const complete = record["complete"];
  const version = record["version"];
  const todoId = record["todoId"];
  if (
    typeof todoId !== "string" || !UUID_PATTERN.test(todoId) ||
    !isRating(effortRating) || !isRating(progressRating) ||
    typeof comment !== "string" || comment.length > 2000 ||
    typeof complete !== "boolean" ||
    typeof version !== "number" || !Number.isInteger(version) || version < 0
  ) return null;

  const recognitions = record["recognitions"];
  if (recognitions !== undefined) {
    if (
      !Array.isArray(recognitions) ||
      recognitions.length > 3 ||
      recognitions.some((recognition) =>
        typeof recognition !== "string" ||
        !TUTOR_RECOGNITIONS.includes(recognition as typeof TUTOR_RECOGNITIONS[number]),
      ) ||
      new Set(recognitions).size !== recognitions.length
    ) return null;
  }

  const readyForReviewAt = record["readyForReviewAt"];
  if (
    readyForReviewAt !== undefined &&
    readyForReviewAt !== null &&
    !isOffsetDateTime(readyForReviewAt)
  ) return null;

  return {
    todoId,
    effortRating,
    progressRating,
    comment,
    complete,
    version,
    ...(recognitions === undefined ? {} : { recognitions: recognitions as NonNullable<PracticeReviewInput["recognitions"]> }),
    ...(readyForReviewAt === undefined ? {} : { readyForReviewAt }),
  };
}

function isOffsetDateTime(value: unknown): value is string {
  return typeof value === "string" &&
    OFFSET_DATE_TIME_PATTERN.test(value) &&
    Number.isFinite(Date.parse(value));
}

function isRating(value: unknown): value is number | null {
  return value === null || (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 5
  );
}
